import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

/**
 * Esegue una chiamata al BE e ne gestisce lo stato.
 *
 * - la chiamata riparte quando cambiano i params (confrontati per valore) o con reload()
 * - se i params cambiano mentre una chiamata è in corso, quella vecchia viene annullata
 * - durante il ricaricamento i dati precedenti restano disponibili (il grafico non sparisce)
 *
 * @param {Function} fetcher (params, signal) => Promise<data>
 * @param {Object} params parametri della chiamata
 * @param {Boolean} enabled se false la chiamata non viene fatta (es. utente senza permessi)
 */
export default function useRequest(fetcher, params, enabled = true) {
    const [state, setState] = useState({ data: null, loading: enabled, error: null });
    const [reloadCount, setReloadCount] = useState(0);
    const fetcherRef = useRef(fetcher);
    fetcherRef.current = fetcher;

    const paramsKey = JSON.stringify(params === undefined ? null : params);

    useEffect(() => {
        if (!enabled) return undefined;

        const controller = new AbortController();
        setState(prev => ({ ...prev, loading: true, error: null }));

        fetcherRef.current(JSON.parse(paramsKey), controller.signal)
            .then(data => setState({ data, loading: false, error: null }))
            .catch(error => {
                if (axios.isCancel(error)) return;
                setState(prev => ({ ...prev, loading: false, error }));
            });

        return () => controller.abort();
    }, [paramsKey, enabled, reloadCount]);

    const reload = useCallback(() => setReloadCount(count => count + 1), []);

    return { ...state, reload };
}
